import React, { useState, useEffect } from 'react';
import { getIncidentConfig, updateIncidentConfig, getIncidentCategories } from '../lib/api';
import { useAdminMode } from '../context/AdminModeContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import {
  Settings,
  AlertTriangle,
  Heart,
  Building2,
  Wrench,
  Clipboard,
  Users,
  Cloud,
  Shield,
  Loader2,
  Save,
  RefreshCw,
  Zap,
  Edit2,
  Check,
  X
} from 'lucide-react';

// Category icons
const CATEGORY_ICONS = {
  abuse_misconduct: AlertTriangle,
  injury_medical: Heart,
  facility_premises: Building2,
  equipment: Wrench,
  operational: Clipboard,
  behavior_conduct: Users,
  environmental: Cloud,
  other_safety: Shield,
};

const CATEGORY_LABELS = {
  abuse_misconduct: 'Abuse & Misconduct',
  injury_medical: 'Injury / Medical',
  facility_premises: 'Facility / Premises',
  equipment: 'Equipment',
  operational: 'Operational / Administrative',
  behavior_conduct: 'Behavior / Code of Conduct',
  environmental: 'Environmental / Weather',
  other_safety: 'Other Safety Concern',
};

const DEFAULT_TERMINOLOGY = {
  subject_label: 'Athlete / Participant',
  affiliation_label: 'Team / Organization',
  coach_label: 'Coach',
  staff_label: 'Staff',
  witness_label: 'Witness',
  guardian_label: 'Parent / Guardian',
};

export default function IncidentConfiguration({ organizationId }) {
  const { selectedOrg } = useAdminMode();
  const { user, isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState(null);
  
  // Config state
  const [enabledCategories, setEnabledCategories] = useState([]);
  const [terminology, setTerminology] = useState(DEFAULT_TERMINOLOGY);
  const [autoEscalateCritical, setAutoEscalateCritical] = useState(true);
  const [autoEscalateAbuse, setAutoEscalateAbuse] = useState(true);
  const [editingTerm, setEditingTerm] = useState(null);
  const [tempTermValue, setTempTermValue] = useState('');
  
  const orgId = organizationId || selectedOrg?.id || user?.organization_id;
  
  useEffect(() => {
    const fetchConfig = async () => {
      if (!orgId) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const [configRes, categoriesRes] = await Promise.all([
          getIncidentConfig(orgId),
          getIncidentCategories()
        ]);
        
        const config = configRes.data;
        setEnabledCategories(config.enabled_categories || Object.keys(CATEGORY_LABELS));
        setTerminology({ ...DEFAULT_TERMINOLOGY, ...config.terminology });
        setAutoEscalateCritical(config.auto_escalate_critical ?? true);
        setAutoEscalateAbuse(config.auto_escalate_abuse ?? true);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Failed to load config:', error);
        toast.error('Failed to load configuration');
      } finally {
        setLoading(false);
      }
    };
    
    fetchConfig();
  }, [orgId]);
  
  const handleSave = async () => {
    if (!orgId) return;
    
    setSaving(true);
    try {
      await updateIncidentConfig(orgId, {
        enabled_categories: enabledCategories,
        terminology: terminology,
        auto_escalate_critical: autoEscalateCritical,
        auto_escalate_abuse: autoEscalateAbuse,
      });
      toast.success('Configuration saved');
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };
  
  const toggleCategory = (categoryKey) => {
    setEnabledCategories(prev => 
      prev.includes(categoryKey)
        ? prev.filter(c => c !== categoryKey)
        : [...prev, categoryKey]
    );
  };
  
  const startEditTerm = (termKey) => {
    setEditingTerm(termKey);
    setTempTermValue(terminology[termKey] || '');
  };
  
  const saveTerm = () => {
    if (editingTerm && tempTermValue.trim()) {
      setTerminology(prev => ({ ...prev, [editingTerm]: tempTermValue.trim() }));
    }
    setEditingTerm(null);
    setTempTermValue('');
  };
  
  const cancelEditTerm = () => {
    setEditingTerm(null);
    setTempTermValue('');
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (!orgId) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
          <h3 className="font-semibold text-lg mb-2">No Organization Selected</h3>
          <p className="text-muted-foreground">
            Please select an organization to configure incident settings.
          </p>
        </CardContent>
      </Card>
    );
  }
  
  if (!isSuperAdmin()) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-8 text-center">
          <Shield className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="font-semibold text-lg mb-2">Super Admin Access Required</h3>
          <p className="text-muted-foreground">
            Only Super Admins can modify incident configuration settings.
          </p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="incident-configuration">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
            <Settings className="w-6 h-6" />
            Incident Configuration
          </h2>
          <p className="text-muted-foreground text-sm">
            Customize incident categories and settings for {selectedOrg?.name || 'this organization'}
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="btn-primary-glow gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Changes
        </Button>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Management */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight">
              Incident Categories
            </CardTitle>
            <CardDescription>
              Enable or disable incident categories for this organization
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(CATEGORY_LABELS).map(([key, label]) => {
              const Icon = CATEGORY_ICONS[key] || Shield;
              const isEnabled = enabledCategories.includes(key);
              const isAbuse = key === 'abuse_misconduct';
              
              return (
                <div 
                  key={key}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    isEnabled ? 'border-primary/30 bg-primary/5' : 'border-border bg-secondary/20'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isEnabled ? 'bg-primary/20' : 'bg-secondary'}`}>
                      <Icon className={`w-5 h-5 ${isEnabled ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    <div>
                      <p className={`font-medium ${isEnabled ? '' : 'text-muted-foreground'}`}>{label}</p>
                      {isAbuse && (
                        <Badge className="bg-red-500/20 text-red-500 text-xs mt-1">Requires Legal</Badge>
                      )}
                    </div>
                  </div>
                  <Switch
                    checked={isEnabled}
                    onCheckedChange={() => toggleCategory(key)}
                    disabled={isAbuse} // Abuse category should always be enabled
                  />
                </div>
              );
            })}
          </CardContent>
        </Card>
        
        {/* Terminology Customization */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight">
              Terminology
            </CardTitle>
            <CardDescription>
              Customize labels used in incident forms
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(terminology).map(([key, value]) => {
              const isEditing = editingTerm === key;
              const displayLabel = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              
              return (
                <div key={key} className="flex items-center justify-between p-3 rounded-lg border border-border">
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground mb-1">{displayLabel}</p>
                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={tempTermValue}
                          onChange={(e) => setTempTermValue(e.target.value)}
                          className="h-8 bg-secondary/50"
                          autoFocus
                        />
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={saveTerm}>
                          <Check className="w-4 h-4 text-green-500" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={cancelEditTerm}>
                          <X className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ) : (
                      <p className="font-medium">{value}</p>
                    )}
                  </div>
                  {!isEditing && (
                    <Button 
                      size="icon" 
                      variant="ghost" 
                      className="h-8 w-8"
                      onClick={() => startEditTerm(key)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
        
        {/* Auto-Escalation Rules */}
        <Card className="bg-card border-border lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-500" />
              Auto-Escalation Rules
            </CardTitle>
            <CardDescription>
              Automatically escalate certain incidents for immediate attention
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={`flex items-center justify-between p-4 rounded-lg border ${
                autoEscalateCritical ? 'border-orange-500/30 bg-orange-500/5' : 'border-border'
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${autoEscalateCritical ? 'bg-orange-500/20' : 'bg-secondary'}`}>
                    <AlertTriangle className={`w-5 h-5 ${autoEscalateCritical ? 'text-orange-500' : 'text-muted-foreground'}`} />
                  </div>
                  <div>
                    <p className="font-medium">Critical Severity</p>
                    <p className="text-sm text-muted-foreground">Auto-escalate all critical severity incidents</p>
                  </div>
                </div>
                <Switch
                  checked={autoEscalateCritical}
                  onCheckedChange={setAutoEscalateCritical}
                />
              </div>
              
              <div className={`flex items-center justify-between p-4 rounded-lg border ${
                autoEscalateAbuse ? 'border-red-500/30 bg-red-500/5' : 'border-border'
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${autoEscalateAbuse ? 'bg-red-500/20' : 'bg-secondary'}`}>
                    <Shield className={`w-5 h-5 ${autoEscalateAbuse ? 'text-red-500' : 'text-muted-foreground'}`} />
                  </div>
                  <div>
                    <p className="font-medium">Abuse & Misconduct</p>
                    <p className="text-sm text-muted-foreground">Auto-escalate all abuse/misconduct incidents</p>
                  </div>
                </div>
                <Switch
                  checked={autoEscalateAbuse}
                  onCheckedChange={setAutoEscalateAbuse}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
